import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, computed, inject, signal } from '@angular/core';
import {
    AbstractControl,
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { marker } from '@colsen1991/ngx-translate-extract-marker';
import { CompanyCodeDataService, ICompanyCode } from '@lib/company-code';
import { NotificationMessageLevel, NotificationService } from '@lib/core';
import { BaseSiteLayoutModule } from '@lib/util/base-site-layout';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { CreatePurchasingGroupCommand } from '../models/create-purchasing-group-command.model';
import { PurchasingGroup } from '../models/purchasing-group.model';
import { PurchasingGroupDataService } from '../services/purchasing-group-data.service';

interface IPurchasingGroupCreateForm {
    readonly code: FormControl<string>;
    readonly name: FormControl<string>;
    readonly description: FormControl<string | null>;
    readonly companyCodeIds: FormControl<readonly string[]>;
}

export class PurchasingGroupCreateDialogResult {
    constructor(
        public readonly created: boolean,
        public readonly purchasingGroup: PurchasingGroup | null = null,
    ) {}
}

@Component({
    selector: 'lib-purchasing-group-create',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, MatDialogModule, BaseSiteLayoutModule, TranslateModule],
    templateUrl: './purchasing-group-create.component.html',
    styleUrls: ['./purchasing-group-create.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PurchasingGroupCreateComponent implements OnInit {
    private readonly companyCodeDataService = inject(CompanyCodeDataService);
    private readonly fb = inject(FormBuilder);
    private readonly dataService = inject(PurchasingGroupDataService);
    private readonly notificationService = inject(NotificationService);
    private readonly dialogRef =
        inject<MatDialogRef<PurchasingGroupCreateComponent, PurchasingGroupCreateDialogResult>>(MatDialogRef);

    public readonly heading = marker('Einkäufergruppe anlegen');
    public readonly icon = 'fas fa-users';
    public readonly saving = signal(false);
    public readonly companyCodesLoading = signal(false);
    public readonly companyCodes = signal<readonly ICompanyCode[]>([]);
    public readonly selectedCompanyCodeIds = signal<readonly string[]>([]);
    public readonly selectedCompanyCodeCount = computed(() => this.selectedCompanyCodeIds().length);
    public readonly pendingCompanyCodeId = signal<string>('');
    public readonly availableCompanyCodes = computed<readonly ICompanyCode[]>(() => {
        const selected = new Set(this.selectedCompanyCodeIds());
        return this.companyCodes().filter((companyCode) => !selected.has(companyCode.id));
    });
    public readonly selectedCompanyCodes = computed<readonly ICompanyCode[]>(() => {
        const byId = new Map(this.companyCodes().map((companyCode) => [companyCode.id, companyCode]));
        return this.selectedCompanyCodeIds()
            .map((id) => byId.get(id) ?? ({ id, code: id, name: id } satisfies ICompanyCode))
            .sort((left, right) => left.code.localeCompare(right.code, 'de', { sensitivity: 'base' }));
    });

    public readonly form: FormGroup<IPurchasingGroupCreateForm> = this.fb.group<IPurchasingGroupCreateForm>({
        code: this.fb.control('', {
            nonNullable: true,
            validators: [this.trimmedRequiredValidator(), Validators.maxLength(30)],
        }),
        name: this.fb.control('', {
            nonNullable: true,
            validators: [this.trimmedRequiredValidator(), Validators.maxLength(100)],
        }),
        description: this.fb.control<string | null>(null, {
            nonNullable: false,
            validators: [Validators.maxLength(250)],
        }),
        companyCodeIds: this.fb.control<readonly string[]>([], { nonNullable: true }),
    });

    public ngOnInit(): void {
        this.loadCompanyCodes();
    }

    public addCompanyCode(): void {
        const id = this.pendingCompanyCodeId();
        if (!id || this.selectedCompanyCodeIds().includes(id)) {
            return;
        }

        const next = [...this.selectedCompanyCodeIds(), id];
        this.selectedCompanyCodeIds.set(next);
        this.form.controls.companyCodeIds.setValue(next, { emitEvent: false });
        this.form.controls.companyCodeIds.markAsDirty();
        this.pendingCompanyCodeId.set('');
    }

    public removeCompanyCode(id: string): void {
        const next = this.selectedCompanyCodeIds().filter((entry) => entry !== id);
        this.selectedCompanyCodeIds.set(next);
        this.form.controls.companyCodeIds.setValue(next, { emitEvent: false });
        this.form.controls.companyCodeIds.markAsDirty();
    }

    public save(): void {
        if (this.form.invalid) {
            this.form.markAllAsTouched();
            return;
        }

        this.saving.set(true);

        const command = this.createCommand();
        this.dataService
            .create(command)
            .pipe(finalize(() => this.saving.set(false)))
            .subscribe({
                next: () => {
                    this.notificationService.showNotification(
                        marker('Einkäufergruppe wurde angelegt'),
                        NotificationMessageLevel.SUCCESS,
                    );
                    this.dialogRef.close(new PurchasingGroupCreateDialogResult(true));
                },
                error: (error) => {
                    if (this.dataService.isDuplicateCodeError(error)) {
                        this.applyDuplicateCodeError();
                    }

                    const message = this.dataService.isDuplicateCodeError(error)
                        ? marker('Code ist bereits vergeben')
                        : this.dataService.resolveErrorMessage(
                              error,
                              marker('Fehler beim Anlegen der Einkäufergruppe'),
                          );
                    this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                },
            });
    }

    public cancel(): void {
        this.dialogRef.close(new PurchasingGroupCreateDialogResult(false));
    }

    private createCommand(): CreatePurchasingGroupCommand {
        const raw = this.form.getRawValue();
        const description = raw.description?.trim();

        return new CreatePurchasingGroupCommand(
            raw.code.trim(),
            raw.name.trim(),
            description && description.length > 0 ? description : undefined,
            [...this.selectedCompanyCodeIds()],
        );
    }

    private loadCompanyCodes(): void {
        this.companyCodesLoading.set(true);
        this.companyCodeDataService
            .getAllActive()
            .pipe(finalize(() => this.companyCodesLoading.set(false)))
            .subscribe({
                next: (companyCodes) => {
                    this.companyCodes.set(
                        [...companyCodes].sort((left, right) =>
                            left.code.localeCompare(right.code, 'de', { sensitivity: 'base' }),
                        ),
                    );
                },
                error: (error) => {
                    const message = this.companyCodeDataService.resolveErrorMessage(
                        error,
                        marker('Buchungskreise konnten nicht geladen werden.'),
                    );
                    this.notificationService.showNotification(message, NotificationMessageLevel.ERROR);
                },
            });
    }

    private applyDuplicateCodeError(): void {
        const codeControl = this.form.controls.code;
        codeControl.setErrors({ ...codeControl.errors, duplicateCode: true });
        codeControl.markAsTouched();
    }

    private trimmedRequiredValidator(): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const value = typeof control.value === 'string' ? control.value : '';
            return value.trim().length > 0 ? null : { required: true };
        };
    }
}
